import type { Lang } from "./services";

export const UI = {
  nl: {
    services: "Diensten",
    about: "Over ons",
    contact: "Contact",
    urgent: "Direct hulp",
    callUs: "Bel ons",
    mailUs: "Mail ons",
    visitUs: "Bezoek ons",
    heroKicker: "Cybersecurity · Websites · Netwerken",
    heroTitle: "Gehackt, verouderd of onbeschermd? Wij lossen het op.",
    heroText: "Cyscom helpt bedrijven in heel Nederland met website-beveiliging, nieuwe websites, hulp bij hacks, cyber scans, firewalls en veilige bedrijfsnetwerken.",
    alertTitle: "Verouderde PHP of een onveilig CMS?",
    alertText: "Een verouderde PHP-versie of een niet-bijgewerkt CMS kan beveiligingsrisico’s opleveren. Laat controleren welke updates en beveiligingsmaatregelen jouw website nodig heeft.",
    alertCta: "Laat mijn website controleren",
    allServices: "Al onze diensten",
    moreInfo: "Meer informatie",
    whatWeDo: "Wat we voor je doen",
    faq: "Veelgestelde vragen",
    byProvince: "Kies je provincie",
    placesIn: "Plaatsen in",
    neighborhoods: "Wijken",
    nearby: "In de buurt",
    otherServices: "Andere diensten in",
    formTitle: "Vraag direct hulp of advies",
    formText: "Laat je gegevens achter voor hulp of advies.",
    name: "Naam",
    email: "E-mail",
    phone: "Telefoon",
    message: "Waar kunnen we mee helpen?",
    send: "Versturen",
    sending: "Versturen…",
    sent: "Bedankt! We nemen snel contact met je op.",
    sendError: "Versturen lukte niet. Bel of mail ons direct.",
    localTitle: "Lokaal in",
    localText: (place: string, province: string) =>
      `We helpen bedrijven en organisaties in ${place} en de rest van ${province} op afstand en, waar nodig, op locatie.`,
    population: (n: string) => `ongeveer ${n} inwoners`,
    inProvince: "in de provincie",
    partOf: "onderdeel van",
    loading: "Laden…",
    notFound: "Deze pagina bestaat niet.",
    home: "Home",
    rights: "Alle rechten voorbehouden.",
    legal: "Juridisch",
    privacy: "Privacy",
    terms: "Voorwaarden",
    cookies: "Cookies",
  },
  en: {
    services: "Services",
    about: "About",
    contact: "Contact",
    urgent: "Urgent help",
    callUs: "Call us",
    mailUs: "Email us",
    visitUs: "Visit us",
    heroKicker: "Cybersecurity · Websites · Networks",
    heroTitle: "Hacked, outdated or unprotected? We fix it.",
    heroText: "Cyscom helps businesses across the Netherlands with website security, new websites, hack recovery, cyber scans, firewalls and secure business networks.",
    alertTitle: "Outdated PHP or an insecure CMS?",
    alertText: "An outdated PHP version or an unpatched CMS can pose security risks. Have your website checked for the updates and protection it needs.",
    alertCta: "Check my website",
    allServices: "All our services",
    moreInfo: "More information",
    whatWeDo: "What we do for you",
    faq: "Frequently asked questions",
    byProvince: "Choose your province",
    placesIn: "Places in",
    neighborhoods: "Neighbourhoods",
    nearby: "Nearby",
    otherServices: "Other services in",
    formTitle: "Ask for help or advice",
    formText: "Leave your details for help or advice.",
    name: "Name",
    email: "Email",
    phone: "Phone",
    message: "How can we help?",
    send: "Send",
    sending: "Sending…",
    sent: "Thank you! We'll be in touch soon.",
    sendError: "Sending failed. Please call or email us directly.",
    localTitle: "Local in",
    localText: (place: string, province: string) =>
      `We help businesses and organisations in ${place} and across ${province} remotely and, where needed, on site.`,
    population: (n: string) => `about ${n} inhabitants`,
    inProvince: "in the province of",
    partOf: "part of",
    loading: "Loading…",
    notFound: "This page does not exist.",
    home: "Home",
    rights: "All rights reserved.",
    legal: "Legal",
    privacy: "Privacy",
    terms: "Terms",
    cookies: "Cookies",
  },
  fr: {
    services: "Services",
    about: "À propos",
    contact: "Contact",
    urgent: "Aide urgente",
    callUs: "Appelez-nous",
    mailUs: "Écrivez-nous",
    visitUs: "Rendez-nous visite",
    heroKicker: "Cybersécurité · Sites web · Réseaux",
    heroTitle: "Piraté, obsolète ou sans protection ? Nous réglons ça.",
    heroText: "Cyscom aide les entreprises partout aux Pays-Bas : sécurité de site web, nouveaux sites, restauration après piratage, scans, pare-feu et réseaux d'entreprise sécurisés.",
    alertTitle: "PHP obsolète ou CMS non sécurisé ?",
    alertText: "Une version obsolète de PHP ou un CMS non mis à jour peut présenter des risques. Faites vérifier les mises à jour et les protections nécessaires à votre site.",
    alertCta: "Vérifier mon site",
    allServices: "Tous nos services",
    moreInfo: "En savoir plus",
    whatWeDo: "Ce que nous faisons pour vous",
    faq: "Questions fréquentes",
    byProvince: "Choisissez votre province",
    placesIn: "Localités en",
    neighborhoods: "Quartiers",
    nearby: "À proximité",
    otherServices: "Autres services à",
    formTitle: "Demandez de l'aide ou un conseil",
    formText: "Laissez vos coordonnées pour obtenir de l’aide ou un conseil.",
    name: "Nom",
    email: "E-mail",
    phone: "Téléphone",
    message: "Comment pouvons-nous aider ?",
    send: "Envoyer",
    sending: "Envoi…",
    sent: "Merci ! Nous vous recontactons rapidement.",
    sendError: "L'envoi a échoué. Appelez-nous ou écrivez-nous directement.",
    localTitle: "Local à",
    localText: (place: string, province: string) =>
      `Nous aidons les entreprises et organisations de ${place} et de toute la province de ${province}, à distance et sur site si nécessaire.`,
    population: (n: string) => `environ ${n} habitants`,
    inProvince: "dans la province de",
    partOf: "fait partie de",
    loading: "Chargement…",
    notFound: "Cette page n'existe pas.",
    home: "Accueil",
    rights: "Tous droits réservés.",
    legal: "Mentions légales",
    privacy: "Confidentialité",
    terms: "Conditions",
    cookies: "Cookies",
  },
} satisfies Record<Lang, Record<string, unknown>>;

export const ui = (lang: Lang) => UI[lang];
